import { useState } from 'react';
import { useAsync } from '@/lib/useAsync';
import { ErrorState, Spinner } from '@/components/ui/States';
import { Markdown } from '@/components/ui/Markdown';
import { api, type PortalDeliverableDetail } from '@/lib/api';
import { timeAgo } from '@/lib/timeAgo';
import { usePortalConfig } from './PortalLayout';

/* Everything the hive has shipped, in the formats the team works in. */
export function PortalDeliverablesPage() {
  const config = usePortalConfig();
  const rows = useAsync(() => api.listPortalDeliverables(), []);
  const [open, setOpen] = useState<PortalDeliverableDetail | null>(null);
  const [loadingId, setLoadingId] = useState<string | null>(null);

  if (rows.status === 'loading') return <Spinner label="Loading deliverables…" />;
  if (rows.status === 'error') return <ErrorState error={rows.error} onRetry={rows.reload} />;

  async function openDoc(id: string) {
    setLoadingId(id);
    try {
      setOpen(await api.getPortalDeliverable(id));
    } finally {
      setLoadingId(null);
    }
  }

  return (
    <>
      <div className="portal-head">
        {config.region && (
          <div className="portal-geo">
            {config.region} <span className="car">▾</span>
          </div>
        )}
        <h1>Deliverables</h1>
        <p>Every one traces back to the run and the knowledge it was built from.</p>
      </div>

      <div className="portal-panel">
        {rows.data.length === 0 && <p className="body">Nothing shipped yet. Deliverables land here as agents run.</p>}
        {rows.data.map((d) => (
          <div key={d.id} className="portal-row">
            <span className="portal-fmt">{d.format}</span>
            <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {d.title}
            </span>
            <span className="meta">{d.department}</span>
            <span className="meta">{timeAgo(d.updatedAt)}</span>
            <button className="portal-btn-ghost" disabled={loadingId === d.id} onClick={() => void openDoc(d.id)}>
              {loadingId === d.id ? '…' : 'Open'}
            </button>
          </div>
        ))}
      </div>
      <p className="portal-hint">
        Deliverables ship in the format each team works in: PDF for the board, spreadsheets for data. Branded to{' '}
        {config.name} throughout.
      </p>

      {open && (
        <div className="portal-modal" onClick={() => setOpen(null)}>
          <div className="portal-doc" onClick={(e) => e.stopPropagation()}>
            <div className="dochead">
              <h2>{open.title}</h2>
              <p className="sub">
                {open.department} · v{open.version} · {timeAgo(open.updatedAt)}
              </p>
            </div>
            <Markdown>{open.content}</Markdown>
            <div style={{ marginTop: 18, textAlign: 'right' }}>
              <button className="portal-btn-ghost" style={{ color: '#26232a', borderColor: '#d8d5d0' }} onClick={() => setOpen(null)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
